<script setup lang="ts">
import { ref } from 'vue';
import { useLocale } from '../../composables/useLocale';

const { t } = useLocale();

defineProps<{
  userCount: number;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (event: 'setPassword', value: string): void;
}>();

const password = ref('');
const passwordVisible = ref(false);
const repeatPassword = ref('');
const repeatPasswordVisible = ref(false);

const isValid = ref(false);

defineExpose({
  isValid,
});
</script>

<template>
  <v-form v-model="isValid" :disabled="loading">
    <v-row>
      <v-col cols="12">
        <v-alert v-if="!loading" type="warning" variant="outlined" border="start" icon="mdi-alert">
          {{ t('lcp.set_password_warning', { count: userCount }) }}
        </v-alert>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="password"
          @change="emit('setPassword', password)"
          :label="t('lcp.password')"
          :rules="[
            (value: string) => !!value || t('lcp.password_required'),
            (value: string) => value.length >= 8 || t('lcp.password_min_length'),
          ]"
          :type="passwordVisible ? 'text' : 'password'"
          prepend-inner-icon="mdi-lock"
          variant="outlined"
          hide-details="auto"
        >
          <template #append-inner>
            <v-icon
              @click="passwordVisible = !passwordVisible"
              @keyup.enter="passwordVisible = !passwordVisible"
              @keyup.space="passwordVisible = !passwordVisible"
              :aria-label="passwordVisible ? t('lcp.hide_password') : t('lcp.show_password')"
            >
              {{ passwordVisible ? 'mdi-eye' : 'mdi-eye-off' }}
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-model="repeatPassword"
          :label="t('lcp.repeat_password')"
          :type="repeatPasswordVisible ? 'text' : 'password'"
          :rules="[
            (value: string) => !!value || t('lcp.confirm_password_required'),
            (value: string) => value === password || t('lcp.passwords_do_not_match'),
          ]"
          prepend-inner-icon="mdi-lock"
          variant="outlined"
          hide-details="auto"
        >
          <template #append-inner>
            <v-icon
              @click="repeatPasswordVisible = !repeatPasswordVisible"
              @keyup.enter="repeatPasswordVisible = !repeatPasswordVisible"
              @keyup.space="repeatPasswordVisible = !repeatPasswordVisible"
              :aria-label="repeatPasswordVisible ? t('lcp.hide_repeat_password') : t('lcp.show_repeat_password')"
            >
              {{ repeatPasswordVisible ? 'mdi-eye' : 'mdi-eye-off' }}
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
  </v-form>
</template>
